import axios from 'axios'
import { hostUI } from '../../configuraciones'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

const initialState = {
    records: [],
    pagination: {
        totalRecords: 0,
        totalPages: 1,
        currentPage: 1,
        limit: 10
    },
    loading: false,
    error: null
}

const OBTENER_COCCION_LISTA = 'bitacoraCoccion/obtenerLista'
const GUARDAR_COCCION_RECORD = 'bitacoraCoccion/guardarRecord'
const ACTUALIZAR_COCCION_RECORD = 'bitacoraCoccion/actualizarRecord'
const CAMBIAR_ESTATUS_COCCION = 'bitacoraCoccion/cambiarEstatus'
const ELIMINAR_COCCION_RECORD = 'bitacoraCoccion/eliminarRecord'

// GET list of cocción records from BackendInspeccion (hostUI)
export const obtenerCoccionRecords = createAsyncThunk(
    OBTENER_COCCION_LISTA,
    async (params, { rejectWithValue }) => {
        try {
            let idLogin = null;
            let page = 1;
            let limit = 10;
            let search = '';

            if (typeof params === 'object' && params !== null) {
                idLogin = params.idLogin || params.id_login || null;
                page = params.page || 1;
                limit = params.limit || 10;
                search = params.search || '';
            } else {
                idLogin = params;
            }

            const queryParts = [];
            if (idLogin) queryParts.push(`idLogin=${idLogin}`);
            if (page) queryParts.push(`page=${page}`);
            if (limit) queryParts.push(`limit=${limit}`);
            if (search) queryParts.push(`search=${encodeURIComponent(search)}`);

            const queryString = queryParts.length ? `?${queryParts.join('&')}` : '';
            const url = `${hostUI}bitacora-produccion/coccion${queryString}`;

            const res = await axios.get(url);
            return {
                records: res.data.data || [],
                pagination: res.data.pagination || { totalRecords: (res.data.data || []).length, totalPages: 1, currentPage: 1, limit: 10 }
            };
        } catch (error) {
            console.error("Error al obtener registros de cocción:", error);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
)

// POST create new record
export const guardarCoccionRecord = createAsyncThunk(
    GUARDAR_COCCION_RECORD,
    async (recordData, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/coccion`
            const res = await axios.post(url, recordData)
            return res.data.data
        } catch (error) {
            console.error("Error al crear registro de cocción:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

// PUT update existing record
export const actualizarCoccionRecord = createAsyncThunk(
    ACTUALIZAR_COCCION_RECORD,
    async ({ id, recordData }, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/coccion/${id}`
            const res = await axios.put(url, recordData)
            return res.data.data
        } catch (error) {
            console.error("Error al actualizar registro de cocción:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

// PUT update status & observations
export const actualizarEstatusCoccionRecord = createAsyncThunk(
    CAMBIAR_ESTATUS_COCCION,
    async ({ id, estatus, observaciones, observacionesCorreccion, observaciones_correccion }, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/coccion/${id}/estatus`
            const obsCorr = observacionesCorreccion || observaciones_correccion || observaciones;
            const res = await axios.put(url, { estatus, observaciones: obsCorr, observacionesCorreccion: obsCorr, observaciones_correccion: obsCorr })
            return res.data.data
        } catch (error) {
            console.error("Error al actualizar estatus de cocción:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

// DELETE record
export const eliminarCoccionRecord = createAsyncThunk(
    ELIMINAR_COCCION_RECORD,
    async (id, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/coccion/${id}`
            await axios.delete(url)
            return id
        } catch (error) {
            console.error("Error al eliminar registro de cocción:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

const bitacoraCoccionSlice = createSlice({
    name: 'bitacoraCoccion',
    initialState,
    reducers: {
        resetCoccionError: (state) => {
            state.error = null
        }
    },
    extraReducers: (builder) => {
        builder
            // Obtener Lista
            .addCase(obtenerCoccionRecords.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(obtenerCoccionRecords.fulfilled, (state, { payload }) => {
                state.loading = false
                state.records = payload.records
                state.pagination = payload.pagination
            })
            .addCase(obtenerCoccionRecords.rejected, (state, { payload }) => {
                state.loading = false
                state.error = payload
            })

            // Guardar Nuevo
            .addCase(guardarCoccionRecord.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(guardarCoccionRecord.fulfilled, (state, { payload }) => {
                state.loading = false
                state.records.unshift(payload)
            })
            .addCase(guardarCoccionRecord.rejected, (state, { payload }) => {
                state.loading = false
                state.error = payload
            })

            // Actualizar Existente
            .addCase(actualizarCoccionRecord.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(actualizarCoccionRecord.fulfilled, (state, { payload }) => {
                state.loading = false
                const idx = state.records.findIndex(r => r.id === payload.id)
                if (idx !== -1) state.records[idx] = { ...state.records[idx], ...payload }
            })
            .addCase(actualizarCoccionRecord.rejected, (state, { payload }) => {
                state.loading = false
                state.error = payload
            })

            // Cambiar Estatus
            .addCase(actualizarEstatusCoccionRecord.fulfilled, (state, { payload }) => {
                const idx = state.records.findIndex(r => r.id === payload.id)
                if (idx !== -1) {
                    state.records[idx].status = payload.status
                    if (payload.observaciones) state.records[idx].observaciones = payload.observaciones
                }
            })

            // Eliminar
            .addCase(eliminarCoccionRecord.fulfilled, (state, { payload }) => {
                state.records = state.records.filter(r => r.id !== payload)
            })
    }
})

export const { resetCoccionError } = bitacoraCoccionSlice.actions
export default bitacoraCoccionSlice.reducer
